/**
 * /valore-immobiliare — Magazine editoriale interno di ValutaGenio.
 *
 * Layout: testata + hero article + card + sezione categorie.
 * Renderer purely locale (no CMS): legge i dati da
 * ``src/content/magazine/index.js``.
 */
import React, { useEffect, useState } from "react";
import { Link, useParams, Navigate } from "react-router-dom";
import { ArrowRight, ArrowLeft, Clock, Calendar } from "lucide-react";
import { LandingNavbar } from "@/pages/OwnerLanding";
import Footer from "@/components/landing/Footer";
import {
  ARTICLES,
  CATEGORIES,
  MAGAZINE_META,
  getArticleBySlug,
  getCategoryLabel,
  getLatestArticles,
  getPublishedCategories,
  getSiblingArticle,
  getArticlesByCategory,
} from "@/content/magazine";

// ---------- shared building blocks --------------------------------------

function Masthead() {
  return (
    <header
      data-testid="magazine-masthead"
      className="bg-[#1F2937] text-white border-b border-black/10"
    >
      <div className="max-w-[1200px] mx-auto px-5 md:px-8 py-14 md:py-20">
        <p
          data-testid="magazine-signature"
          className="uppercase tracking-[0.24em] text-[11px] md:text-xs text-white/60"
        >
          {MAGAZINE_META.signature}
        </p>
        <h1
          data-testid="magazine-name"
          className="mt-3 md:mt-4 font-display font-extrabold tracking-tight leading-[1.02] text-5xl sm:text-6xl md:text-7xl lg:text-[88px]"
        >
          {MAGAZINE_META.name}
        </h1>
        <p
          data-testid="magazine-tagline"
          className="mt-4 md:mt-5 text-lg md:text-xl text-white/80 font-light"
        >
          {MAGAZINE_META.tagline}
        </p>
      </div>
    </header>
  );
}

function CategoryChip({ slug, testid }) {
  return (
    <span
      data-testid={testid}
      className="inline-flex items-center uppercase tracking-[0.18em] text-[10px] md:text-[11px] font-semibold text-[#B40101]"
    >
      {getCategoryLabel(slug)}
    </span>
  );
}

function MetaLine({ article, testid }) {
  return (
    <div
      data-testid={testid}
      className="flex items-center gap-4 text-xs md:text-[13px] text-[#0A0A0A]/70"
    >
      <span className="inline-flex items-center gap-1.5">
        <Calendar className="w-3.5 h-3.5" strokeWidth={1.8} />
        {article.publish_date_label}
      </span>
      {article.reading_time_min && (
        <span className="inline-flex items-center gap-1.5">
          <Clock className="w-3.5 h-3.5" strokeWidth={1.8} />
          {article.reading_time_min} min di lettura
        </span>
      )}
    </div>
  );
}

// ---------- 1. INDEX ----------------------------------------------------

function MagazineHero({ article }) {
  return (
    <article
      data-testid="magazine-hero-article"
      className="border-b border-[#E5E7EB]"
    >
      <Link
        to={`${MAGAZINE_META.base_path}/${article.slug}`}
        className="block group"
        data-testid="magazine-hero-link"
      >
        <div className="max-w-[1200px] mx-auto px-5 md:px-8 py-12 md:py-16 grid lg:grid-cols-[minmax(0,1.05fr)_minmax(0,1fr)] gap-10 lg:gap-14 items-center">
          <div className="order-2 lg:order-1">
            <CategoryChip
              slug={article.category_slug}
              testid="magazine-hero-category"
            />
            <h2
              data-testid="magazine-hero-title"
              className="mt-4 font-display font-extrabold text-[#1F2937] tracking-tight leading-[1.05] text-3xl sm:text-4xl md:text-5xl lg:text-[52px] group-hover:text-[#B40101] transition-colors"
            >
              {article.title}
            </h2>
            <p
              data-testid="magazine-hero-summary"
              className="mt-5 md:mt-6 text-[#1F2937] text-lg md:text-xl leading-relaxed font-light"
            >
              {article.summary}
            </p>
            <div className="mt-6">
              <MetaLine article={article} testid="magazine-hero-meta" />
            </div>
            <div className="mt-8 inline-flex items-center gap-2 text-[#B40101] font-semibold">
              Leggi l’articolo
              <ArrowRight className="w-4 h-4" strokeWidth={2.2} />
            </div>
          </div>
          <div className="order-1 lg:order-2">
            <div className="relative rounded-2xl overflow-hidden shadow-md ring-1 ring-black/5 aspect-[16/9]">
              <img
                src={article.cover_image.src}
                alt={article.cover_image.alt}
                width={article.cover_image.width}
                height={article.cover_image.height}
                loading="eager"
                decoding="async"
                data-testid="magazine-hero-image"
                className="w-full h-full object-cover"
              />
            </div>
          </div>
        </div>
      </Link>
    </article>
  );
}

function MagazineCard({ article, testid }) {
  return (
    <article
      data-testid={testid}
      className="bg-white rounded-2xl overflow-hidden shadow-sm ring-1 ring-black/5 hover:shadow-md transition-shadow"
    >
      <Link
        to={`${MAGAZINE_META.base_path}/${article.slug}`}
        className="block group"
      >
        <div className="aspect-[16/9] overflow-hidden">
          <img
            src={article.cover_image.src}
            alt={article.cover_image.alt}
            width={article.cover_image.width}
            height={article.cover_image.height}
            loading="lazy"
            decoding="async"
            className="w-full h-full object-cover group-hover:scale-[1.02] transition-transform duration-500"
          />
        </div>
        <div className="p-6 md:p-7">
          <CategoryChip slug={article.category_slug} />
          <h3 className="mt-3 font-display font-extrabold text-[#1F2937] tracking-tight leading-[1.1] text-2xl md:text-3xl group-hover:text-[#B40101] transition-colors">
            {article.title}
          </h3>
          <p className="mt-3 text-[#1F2937] text-base leading-relaxed font-light line-clamp-3">
            {article.summary}
          </p>
          <div className="mt-5">
            <MetaLine article={article} />
          </div>
        </div>
      </Link>
    </article>
  );
}

function CategoryFilterBar({ selected, onChange }) {
  return (
    <section
      data-testid="magazine-category-filter"
      className="bg-[#FAFAF7] border-b border-[#E5E7EB]"
    >
      <div className="max-w-[1200px] mx-auto px-5 md:px-8 py-8 md:py-10">
        <p className="uppercase tracking-[0.2em] text-[11px] md:text-xs font-semibold text-[#B40101]">
          Categorie
        </p>
        <ul className="mt-4 flex flex-wrap gap-2 md:gap-3">
          <li>
            <button
              type="button"
              onClick={() => onChange(null)}
              data-testid="magazine-category-all"
              aria-pressed={selected === null}
              className={`inline-flex items-center px-4 py-2 rounded-full text-sm md:text-base font-medium transition-colors ring-1 ${
                selected === null
                  ? "bg-[#B40101] text-white ring-[#B40101]"
                  : "bg-white text-[#1F2937] ring-[#E5E7EB] hover:ring-[#B40101] hover:text-[#B40101]"
              }`}
            >
              Tutti
            </button>
          </li>
          {CATEGORIES.map((c) => {
            const active = selected === c.slug;
            return (
              <li key={c.slug}>
                <button
                  type="button"
                  onClick={() => onChange(c.slug)}
                  data-testid={`magazine-category-${c.slug}`}
                  aria-pressed={active}
                  className={`inline-flex items-center px-4 py-2 rounded-full text-sm md:text-base font-medium transition-colors ring-1 ${
                    active
                      ? "bg-[#B40101] text-white ring-[#B40101]"
                      : "bg-white text-[#1F2937] ring-[#E5E7EB] hover:ring-[#B40101] hover:text-[#B40101]"
                  }`}
                >
                  {c.label}
                </button>
              </li>
            );
          })}
        </ul>
      </div>
    </section>
  );
}

function EmptyCategoryState({ category }) {
  const label = getCategoryLabel(category);
  return (
    <section
      data-testid="magazine-empty-category"
      className="bg-white border-b border-[#E5E7EB]"
    >
      <div className="max-w-[900px] mx-auto px-5 md:px-8 py-20 md:py-28 text-center">
        <p className="uppercase tracking-[0.2em] text-[11px] md:text-xs font-semibold text-[#B40101]">
          {label}
        </p>
        <h2 className="mt-3 font-display font-extrabold text-[#1F2937] tracking-tight text-2xl md:text-3xl leading-[1.2]">
          Prossimamente in questa categoria.
        </h2>
        <p className="mt-4 text-[#1F2937]/75 text-base md:text-lg leading-relaxed max-w-xl mx-auto">
          Stiamo scrivendo i prossimi articoli su questo tema. Nel frattempo, sfoglia le altre categorie.
        </p>
      </div>
    </section>
  );
}

function MagazineFinalCta() {
  return (
    <section
      data-testid="magazine-final-cta"
      className="bg-white"
    >
      <div className="max-w-[900px] mx-auto px-5 md:px-8 py-16 md:py-24 text-center">
        <h2 className="font-display font-extrabold text-[#1F2937] tracking-tight text-3xl md:text-5xl leading-[1.1]">
          Il tuo immobile ha già una storia da leggere.
        </h2>
        <p className="mt-5 md:mt-6 text-[#1F2937] text-lg md:text-xl leading-relaxed font-light">
          Da qui parte l’analisi guidata di ValutaGenio: valore, potenziale,
          scenari e prossimo passo, disegnati intorno alla tua situazione.
        </p>
        <div className="mt-8 md:mt-10">
          <a
            href="/#inizia"
            data-testid="magazine-final-cta-button"
            className="inline-flex items-center gap-3 bg-[#B40101] hover:bg-[#8F0101] text-white font-semibold text-base md:text-lg px-6 md:px-8 py-3.5 md:py-4 rounded-xl transition-colors shadow-sm"
          >
            <span>Inizia dal tuo immobile</span>
            <ArrowRight className="w-5 h-5" strokeWidth={2.2} />
          </a>
        </div>
      </div>
    </section>
  );
}

export function MagazineIndex() {
  useEffect(() => {
    document.title = `${MAGAZINE_META.name} — ${MAGAZINE_META.tagline}`;
    const meta = document.querySelector('meta[name="description"]')
      || Object.assign(document.createElement("meta"), { name: "description" });
    meta.setAttribute("content",
      "Storie, verifiche e strumenti per capire il valore reale di una casa. Un progetto ValutaGenio.");
    document.head.appendChild(meta);
    const og = {
      "og:title": `${MAGAZINE_META.name} — ${MAGAZINE_META.tagline}`,
      "og:description": "Storie, verifiche e strumenti per capire il valore reale di una casa. Un progetto ValutaGenio.",
      "og:type": "website",
    };
    Object.entries(og).forEach(([k, v]) => {
      const tag = document.querySelector(`meta[property="${k}"]`)
        || Object.assign(document.createElement("meta"), { property: k });
      tag.setAttribute("content", v);
      document.head.appendChild(tag);
    });
  }, []);

  const [selectedCategory, setSelectedCategory] = useState(null);
  const filtered = getArticlesByCategory(selectedCategory);
  const [hero, ...rest] = filtered;
  return (
    <main data-testid="magazine-index-page" className="min-h-screen bg-white">
      <LandingNavbar />
      <Masthead />
      <CategoryFilterBar
        selected={selectedCategory}
        onChange={setSelectedCategory}
      />
      {hero && <MagazineHero article={hero} />}
      {!!rest.length && (
        <section
          data-testid="magazine-cards-grid"
          className="bg-white border-b border-[#E5E7EB]"
        >
          <div className="max-w-[1200px] mx-auto px-5 md:px-8 py-12 md:py-16">
            <p className="uppercase tracking-[0.2em] text-[11px] md:text-xs font-semibold text-[#B40101]">
              Ultime uscite
            </p>
            <h2 className="mt-3 font-display font-extrabold text-[#1F2937] tracking-tight text-2xl md:text-3xl lg:text-4xl">
              Altre letture
            </h2>
            <div className="mt-8 grid md:grid-cols-2 gap-6 md:gap-8">
              {rest.map((a, i) => (
                <MagazineCard
                  key={a.slug}
                  article={a}
                  testid={`magazine-card-${i + 1}`}
                />
              ))}
            </div>
          </div>
        </section>
      )}
      {!hero && <EmptyCategoryState category={selectedCategory} />}
      <MagazineFinalCta />
      <Footer />
    </main>
  );
}

// ---------- 2. ARTICLE --------------------------------------------------

function Block({ block, k }) {
  switch (block.type) {
    case "h2":
      return (
        <h2 className="mt-14 md:mt-16 font-display font-extrabold text-[#1F2937] tracking-tight text-3xl md:text-4xl leading-[1.15]">
          {block.text}
        </h2>
      );
    case "h3":
      return (
        <h3 className="mt-12 md:mt-14 font-display font-extrabold text-[#1F2937] tracking-tight text-xl md:text-2xl lg:text-[26px] leading-[1.2]">
          {block.text}
        </h3>
      );
    case "p":
      return (
        <p className="mt-5 md:mt-6 text-[#1F2937] text-[17px] md:text-lg leading-[1.75]">
          {block.text}
        </p>
      );
    case "ul":
      return (
        <ul className="mt-5 md:mt-6 space-y-2.5 pl-5 list-disc marker:text-[#B40101] text-[#1F2937] text-[17px] md:text-lg leading-[1.7]">
          {block.items.map((it, i) => (
            <li key={i} className="pl-1">{it}</li>
          ))}
        </ul>
      );
    case "ol":
      return (
        <ol className="mt-5 md:mt-6 space-y-2.5 pl-5 list-decimal marker:text-[#B40101] marker:font-semibold text-[#1F2937] text-[17px] md:text-lg leading-[1.7]">
          {block.items.map((it, i) => (
            <li key={i} className="pl-1">{it}</li>
          ))}
        </ol>
      );
    case "focus":
      return (
        <aside
          className="mt-10 md:mt-12 rounded-2xl border-l-4 border-[#B40101] bg-[#FFF8F6] p-6 md:p-7"
          data-testid={`article-focus-${k}`}
        >
          {block.title && (
            <p className="font-display font-extrabold text-[#1F2937] tracking-tight text-lg md:text-xl">
              {block.title}
            </p>
          )}
          <p className={`${block.title ? "mt-2" : ""} text-[#1F2937] text-base md:text-lg leading-relaxed font-light`}>
            {block.text}
          </p>
        </aside>
      );
    default:
      return null;
  }
}

export function MagazineArticle() {
  const { slug } = useParams();
  const article = getArticleBySlug(slug);
  const sibling = article ? getSiblingArticle(article.slug) : null;

  useEffect(() => {
    if (!article) return;
    document.title = `${article.title} — ${MAGAZINE_META.name}`;

    const ensure = (selector, ctor, attrs = {}) => {
      let tag = document.querySelector(selector);
      if (!tag) {
        tag = document.createElement(ctor);
        Object.entries(attrs).forEach(([k, v]) => tag.setAttribute(k, v));
        document.head.appendChild(tag);
      }
      return tag;
    };

    const desc = ensure('meta[name="description"]', "meta", { name: "description" });
    desc.setAttribute("content", article.seo.description);

    const setOg = (prop, value) => {
      const tag = ensure(`meta[property="${prop}"]`, "meta", { property: prop });
      tag.setAttribute("content", value);
    };
    setOg("og:title", article.title);
    setOg("og:description", article.seo.description);
    setOg("og:type", "article");
    setOg("og:image", article.seo.og_image_absolute);
    setOg("article:section", article.category_label);
    setOg("article:published_time", article.publish_date);

    // JSON-LD Article
    let ld = document.querySelector('script[data-testid="article-jsonld"]');
    if (!ld) {
      ld = document.createElement("script");
      ld.setAttribute("type", "application/ld+json");
      ld.setAttribute("data-testid", "article-jsonld");
      document.head.appendChild(ld);
    }
    ld.textContent = JSON.stringify({
      "@context": "https://schema.org",
      "@type": "Article",
      headline: article.title,
      description: article.seo.description,
      image: [article.seo.og_image_absolute],
      datePublished: article.publish_date,
      articleSection: article.category_label,
      publisher: {
        "@type": "Organization",
        name: MAGAZINE_META.name,
        parentOrganization: { "@type": "Organization", name: "ValutaGenio" },
      },
    });

    return () => {
      if (ld && ld.parentNode) ld.parentNode.removeChild(ld);
    };
  }, [article]);

  if (!article) return <Navigate to={MAGAZINE_META.base_path} replace />;

  return (
    <main
      data-testid={`magazine-article-page-${article.slug}`}
      className="min-h-screen bg-white"
    >
      <LandingNavbar />

      {/* Hero */}
      <section className="bg-[#FAFAF7] border-b border-[#E5E7EB]">
        <div className="max-w-[1100px] mx-auto px-5 md:px-8 py-10 md:py-14">
          <Link
            to={MAGAZINE_META.base_path}
            data-testid="article-back-to-magazine"
            className="inline-flex items-center gap-2 text-sm md:text-base text-[#0A0A0A]/70 hover:text-[#B40101] transition-colors"
          >
            <ArrowLeft className="w-4 h-4" strokeWidth={2} />
            Torna a {MAGAZINE_META.name}
          </Link>
          <div className="mt-6 md:mt-8">
            <CategoryChip
              slug={article.category_slug}
              testid="article-category"
            />
            <h1
              data-testid="article-title"
              className="mt-4 font-display font-extrabold text-[#1F2937] tracking-tight leading-[1.05] text-3xl sm:text-4xl md:text-5xl lg:text-[56px]"
            >
              {article.title}
            </h1>
            <p
              data-testid="article-summary"
              className="mt-5 md:mt-6 text-[#1F2937] text-lg md:text-xl leading-relaxed font-light max-w-3xl"
            >
              {article.summary}
            </p>
            <div className="mt-5 md:mt-6">
              <MetaLine article={article} testid="article-meta" />
            </div>
          </div>
          <figure className="mt-10 md:mt-12">
            <div className="relative rounded-2xl overflow-hidden shadow-md ring-1 ring-black/5">
              <img
                src={article.cover_image.src}
                alt={article.cover_image.alt}
                width={article.cover_image.width}
                height={article.cover_image.height}
                loading="eager"
                decoding="async"
                data-testid="article-cover-image"
                className="w-full h-auto object-cover"
              />
            </div>
          </figure>
        </div>
      </section>

      {/* Body */}
      <section
        data-testid="article-body"
        className="bg-white"
      >
        <div className="max-w-[720px] mx-auto px-5 md:px-8 py-14 md:py-20">
          {article.blocks
            .filter((b) => b.type !== "h2") // article title already rendered as H1 above
            .map((b, i) => (
              <Block key={i} k={i} block={b} />
            ))}

          {/* Final CTA to the ValutaGenio journey */}
          <div className="mt-14 md:mt-16 rounded-2xl bg-[#1F2937] text-white p-8 md:p-10 text-center">
            <h3 className="font-display font-extrabold tracking-tight text-2xl md:text-3xl leading-[1.2]">
              Costruisci la prima analisi sul tuo immobile.
            </h3>
            <p className="mt-4 text-white/85 text-base md:text-lg leading-relaxed font-light">
              Parti dalla tua situazione. Il percorso guidato di ValutaGenio
              mette insieme valore, potenziale e scenari.
            </p>
            <div className="mt-6 flex justify-center">
              <a
                href="/#inizia"
                data-testid="article-cta-inizia"
                className="inline-flex items-center gap-3 bg-[#B40101] hover:bg-[#8F0101] text-white font-semibold text-base md:text-lg px-6 md:px-8 py-3.5 md:py-4 rounded-xl transition-colors shadow-sm"
              >
                <span>Inizia dal tuo immobile</span>
                <ArrowRight className="w-5 h-5" strokeWidth={2.2} />
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Sibling article */}
      {sibling && (
        <section
          data-testid="article-sibling"
          className="bg-[#FAFAF7] border-t border-[#E5E7EB]"
        >
          <div className="max-w-[1100px] mx-auto px-5 md:px-8 py-14 md:py-20">
            <p className="uppercase tracking-[0.2em] text-[11px] md:text-xs font-semibold text-[#B40101]">
              Continua a leggere
            </p>
            <h2 className="mt-3 font-display font-extrabold text-[#1F2937] tracking-tight text-2xl md:text-3xl lg:text-4xl">
              Un’altra prospettiva
            </h2>
            <div className="mt-6 md:mt-8 max-w-xl">
              <MagazineCard article={sibling} testid="article-sibling-card" />
            </div>
          </div>
        </section>
      )}

      <Footer />
    </main>
  );
}

// ---------- 3. ALIAS /magazine → /valore-immobiliare -------------------

export function MagazineAlias() {
  return <Navigate to={MAGAZINE_META.base_path} replace />;
}

export default MagazineIndex;
